import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../../commonDesign/Button'
import SydInput from '../../../../../commonDesign/SydInput'
import SydLabel from '../../../../../commonDesign/SydLabel'
import { provisionedTargets, useSearchIntegrationJobs } from '../../../../../../api/integrations'
import { decipherError } from '../../../../../../utils/decipherError'

function AssignJobForm ({ target, onCancel }) {
  const [selectedJobId, setSelectedJobId] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')

  const query = useMemo(() => ({
    take: 500,
    includes: { integrationSource: true },
    ...(target?.domain ? { filters: { domain: target.domain } } : {})
  }), [target?.domain])

  const { data: jobsData, isLoading } = useSearchIntegrationJobs(query)
  const { mutateAsync: assignJob } = provisionedTargets.useAssignJobToProvisionedTarget()

  const unassignedJobs = useMemo(() => {
    const jobs = jobsData?.data || []
    return jobs.filter(j => !j.provisionedTargetTableId)
  }, [jobsData])

  const filteredJobs = useMemo(() => {
    if (!search.trim()) return unassignedJobs
    const term = search.toLowerCase()
    return unassignedJobs.filter(j =>
      j.jobKey?.toLowerCase().includes(term) ||
      j.domain?.toLowerCase().includes(term) ||
      j.sourceKey?.toLowerCase().includes(term) ||
      j.integrationSource?.connectionId?.toString().includes(term)
    )
  }, [unassignedJobs, search])

  const handleAssign = useCallback(async () => {
    if (!selectedJobId || !target) return
    try {
      setError(null)
      setProcessing(true)
      await assignJob({
        jobConfigurationId: selectedJobId,
        provisionedTargetTableId: target.provisionedTargetTableId
      })
      onCancel?.()
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [selectedJobId, target, assignJob, onCancel])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SydLabel label='Search Jobs'>
          <SydInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Filter by job key, domain, source key...'
            minWidth='0'
          />
        </SydLabel>
      </Grid>
      <Grid item xs={12}>
        {isLoading ? (
          <div>Loading jobs...</div>
        ) : filteredJobs.length === 0 ? (
          <div style={{ padding: '1rem 0', opacity: 0.6 }}>
            {unassignedJobs.length === 0
              ? 'No unassigned jobs available'
              : 'No jobs match your search'}
          </div>
        ) : (
          <div style={{ maxHeight: '400px', overflow: 'auto' }}>
            <table className='opal-table'>
              <thead>
                <tr>
                  <th />
                  <th>Job Key</th>
                  <th>Domain</th>
                  <th>Source Key</th>
                  <th>Enabled</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map(j => (
                  <tr
                    key={j.jobConfigurationId}
                    onClick={() => setSelectedJobId(j.jobConfigurationId)}
                    style={{ cursor: 'pointer', background: selectedJobId === j.jobConfigurationId ? 'var(--color-primary-light, rgba(0,102,255,0.08))' : undefined }}
                  >
                    <td>
                      <input
                        type='radio'
                        checked={selectedJobId === j.jobConfigurationId}
                        onChange={() => setSelectedJobId(j.jobConfigurationId)}
                      />
                    </td>
                    <td>{j.jobKey}</td>
                    <td>{j.domain}</td>
                    <td>{j.sourceKey}</td>
                    <td>{j.isEnabled ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Grid>
      <Grid item xs={12}>
        <SydModalActions>
          {error ? <div className='__error'>{error}</div> : null}
          <SydButton disabled={processing} variant='ghost' size='md' onClick={onCancel}>
            Cancel
          </SydButton>
          <SydButton
            processing={processing}
            disabled={!selectedJobId}
            variant='primary'
            size='md'
            onClick={handleAssign}
          >
            Assign
          </SydButton>
        </SydModalActions>
      </Grid>
    </Grid>
  )
}

AssignJobForm.propTypes = {
  target: PropTypes.object,
  onCancel: PropTypes.func
}

export default AssignJobForm
